import { Controller } from "@hotwired/stimulus"

// Génère une table des matières à partir des h2/h3 dans l'article.
// Masque le conteneur TOC si moins de 2 titres sont trouvés.
export default class extends Controller {
  static targets = ["article", "list", "root"]
  static values = { minHeadings: { type: Number, default: 2 } }

  connect() {
    if (!this.hasArticleTarget || !this.hasListTarget) return

    const headings = this.articleTarget.querySelectorAll("h2, h3")
    if (headings.length < this.minHeadingsValue) {
      this.hideRoot()
      return
    }

    this.populateList(headings)
    this.observeHeadings(headings)
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
  }

  populateList(headings) {
    const fragment = document.createDocumentFragment()
    const usedIds = new Set()
    headings.forEach((heading, index) => {
      // Fallback `section-N` quand slugify retourne "" (emoji-only, caractères non-latins
      // non couverts par NFD). Suffixe `-2`, `-3`, … pour éviter les collisions sur
      // des titres dupliqués (sinon HTML invalide + IntersectionObserver ambigu).
      const baseId = this.slugify(heading.textContent) || `section-${index + 1}`
      let id = baseId
      let suffix = 2
      while (usedIds.has(id)) {
        id = `${baseId}-${suffix}`
        suffix += 1
      }
      usedIds.add(id)
      heading.id = id
      const li = document.createElement("li")
      li.className = heading.tagName === "H3" ? "toc-item toc-item-h3" : "toc-item"
      const link = document.createElement("a")
      link.href = `#${id}`
      link.textContent = heading.textContent
      link.dataset.tocLink = id
      li.appendChild(link)
      fragment.appendChild(li)
    })
    this.listTarget.replaceChildren(fragment)
  }

  observeHeadings(headings) {
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return
          const id = entry.target.id
          this.listTarget.querySelectorAll("a").forEach((link) => {
            link.classList.toggle("toc-active", link.dataset.tocLink === id)
          })
        })
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: 0 }
    )
    headings.forEach((h) => this.observer.observe(h))
  }

  hideRoot() {
    if (this.hasRootTarget) {
      this.rootTarget.classList.add("hidden")
    } else {
      this.element.classList.add("hidden")
    }
  }

  slugify(text) {
    return text
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
  }
}
